import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Checkbox } from '@/components/ui/Checkbox';
import {
  useDuplicateItem,
  useItems,
  type DuplicateItemInclude,
  type Item,
} from '@/features/inventory/hooks';

interface DuplicateItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The item being cloned. The dialog seeds its fields from this row. */
  source: Item | null;
  /**
   * Called with the new item's id once the clone lands. Defaults to
   * navigating into the new item's detail page when omitted.
   */
  onDuplicated?: (itemId: string) => void;
}

/**
 * Suggest a fresh SKU for the clone by appending a `-COPY` suffix (or
 * `-COPY-2`, `-COPY-3`, …) until we find one that isn't already taken
 * in the catalog. Keeps the operator from immediately bouncing off the
 * unique-SKU error for the common "duplicate then tweak" flow.
 */
function suggestSku(sourceSku: string, existing: Set<string>): string {
  const base = `${sourceSku}-COPY`;
  if (!existing.has(base)) return base;
  let n = 2;
  while (existing.has(`${base}-${n}`)) n += 1;
  return `${base}-${n}`;
}

/**
 * "Duplicate item" modal. Copies an existing SKU's scalar fields and,
 * optionally, its tags / suppliers / BOM / photo into a new item.
 *
 * Tailored to the colourway-variant workflow: the SKU is pre-suggested
 * and the colour is surfaced front-and-centre so the operator can
 * change just the bits that differ. The photo is intentionally
 * unchecked by default — a variant usually gets its own image.
 */
export function DuplicateItemDialog({
  open,
  onOpenChange,
  source,
  onDuplicated,
}: DuplicateItemDialogProps) {
  const navigate = useNavigate();
  const duplicate = useDuplicateItem();
  const { data: existingItems } = useItems();

  const existingSkus = useMemo(() => {
    const set = new Set<string>();
    for (const it of existingItems ?? []) set.add(it.sku);
    return set;
  }, [existingItems]);

  const [sku, setSku] = useState('');
  const [name, setName] = useState('');
  const [color, setColor] = useState('');
  const [include, setInclude] = useState<Required<DuplicateItemInclude>>({
    tags: true,
    suppliers: true,
    bom: true,
    photo: false,
  });

  // Re-seed the form each time the dialog opens for a (possibly
  // different) source row. The SKU suggestion is computed against the
  // current catalog so it doesn't collide out of the gate.
  useEffect(() => {
    if (open && source) {
      setSku(suggestSku(source.sku, existingSkus));
      setName(source.name);
      setColor(source.color ?? '');
      setInclude({ tags: true, suppliers: true, bom: true, photo: false });
    }
    // We only want to re-seed on open / source change, not on every
    // keystroke that mutates the catalog-derived `existingSkus` set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, source?.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!source) return;

    const trimmedSku = sku.trim();
    const trimmedName = name.trim();
    if (!trimmedSku) {
      toast.error('SKU is required.');
      return;
    }
    if (!trimmedName) {
      toast.error('Name is required.');
      return;
    }

    try {
      const { item, warnings } = await duplicate.mutateAsync({
        sourceItemId: source.id,
        overrides: {
          sku: trimmedSku,
          name: trimmedName,
          color: color.trim() || null,
        },
        include,
      });
      if (warnings.length > 0) {
        toast.warning(
          `Created “${item.name}”, but couldn't copy: ${warnings.join(', ')}.`,
        );
      } else {
        toast.success(`Duplicated as “${item.name}”`);
      }
      onOpenChange(false);
      if (onDuplicated) {
        onDuplicated(item.id);
      } else {
        navigate(`/catalog/items/${item.id}`);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not duplicate item.');
    }
  };

  const submitting = duplicate.isPending;
  const includeRows: Array<{ key: keyof DuplicateItemInclude; label: string }> = [
    { key: 'tags', label: 'Tags' },
    { key: 'suppliers', label: 'Suppliers' },
    { key: 'bom', label: 'Bill of materials' },
    { key: 'photo', label: 'Product photo' },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => (!submitting ? onOpenChange(o) : undefined)}>
      <DialogContent className="max-w-lg">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Duplicate item</DialogTitle>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="dup-sku">
                  New SKU <span className="text-fresh-salmon-dark">*</span>
                </Label>
                <Input
                  id="dup-sku"
                  autoFocus
                  value={sku}
                  onChange={(e) => setSku(e.target.value)}
                  maxLength={64}
                  className="font-mono text-xs"
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="dup-color">Color</Label>
                <Input
                  id="dup-color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  placeholder="e.g. Olive"
                  maxLength={64}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="dup-name">
                Name <span className="text-fresh-salmon-dark">*</span>
              </Label>
              <Input
                id="dup-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={200}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Also copy</Label>
              <div className="space-y-2 rounded-md border border-border p-3">
                {includeRows.map((row) => (
                  <label
                    key={row.key}
                    className="flex cursor-pointer items-start gap-3"
                  >
                    <Checkbox
                      ariaLabel={row.label}
                      checked={include[row.key] ?? false}
                      onCheckedChange={(checked) =>
                        setInclude((prev) => ({ ...prev, [row.key]: checked }))
                      }
                      className="mt-0.5"
                    />
                    <span className="text-sm font-medium leading-none">{row.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Duplicating…' : 'Duplicate item'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
